import React from "react";
import { Link } from "react-router-dom";
import useApi from "@/hooks/useApi";
import { PrimaryBlueButton } from "@velaro/velaro-component-lib";
import { LoadingSpinner } from "@velaro/velaro-component-lib";
import TikTokEditModal from "./TikTokEditModal";

interface TikTokConfig {
  id: number;
  openId: string;
  displayName: string;
  username: string;
  avatarUrl: string;
  teamId: number | null;
  teamName: string | null;
  autoCreateContact: boolean;
  tokenValid: boolean;
  tokenExpiresAt: string | null;
  refreshTokenExpiresAt: string | null;
}

interface AppSettings {
  configured: boolean;
  redirectUri: string;
}

export default function TikTokIntegration() {
  const [loading, setLoading] = React.useState(true);
  const [configs, setConfigs] = React.useState<TikTokConfig[]>([]);
  const [editConfig, setEditConfig] = React.useState<TikTokConfig | undefined>();
  const [connecting, setConnecting] = React.useState(false);
  const [appSettings, setAppSettings] = React.useState<AppSettings | null>(null);
  const [message, setMessage] = React.useState<{ text: string; type: "success" | "error" } | null>(null);
  const api = useApi();

  const fetchConfigs = React.useCallback(async () => {
    try {
      const [configsResp, settingsResp] = await Promise.all([
        api.messaging.get("TikTok/Integrations"),
        api.messaging.get("TikTok/AppSettings")
      ]);

      const configsData = await configsResp.json();
      const settingsData = await settingsResp.json();

      setConfigs(Array.isArray(configsData) ? configsData : []);
      setAppSettings(settingsData);
    } catch {
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  }, [api.messaging]);

  React.useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  const handleConnect = React.useCallback(async () => {
    setConnecting(true);
    setMessage(null);
    try {
      const resp = await api.messaging.get(
        `TikTok/OAuth/AuthUrl?returnUrl=${encodeURIComponent(window.location.pathname)}`
      );
      const data = await resp.json();

      if (!resp.ok) {
        setMessage({ text: data.error ?? "Failed to start authorization.", type: "error" });
        return;
      }

      // Redirect to TikTok OAuth — callback page will handle the response
      window.location.href = data.authUrl;
    } catch {
      setMessage({ text: "Failed to start TikTok authorization. Please try again.", type: "error" });
      setConnecting(false);
    }
  }, [api.messaging]);

  const handleRemove = React.useCallback(
    async (id: number) => {
      if (!window.confirm("Disconnect this TikTok account from Velaro?")) return;
      try {
        await api.messaging.delete(`TikTok/Integrations/${id}`);
        fetchConfigs();
      } catch {
        setMessage({ text: "Failed to remove account. Please try again.", type: "error" });
      }
    },
    [api.messaging, fetchConfigs]
  );

  function formatExpiry(dateStr: string | null): string {
    if (!dateStr) return "Unknown";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "Unknown";
    const now = new Date();
    const diff = d.getTime() - now.getTime();
    if (diff < 0) return "Expired";
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days === 0) return "Expires today";
    return `Expires in ${days} day${days === 1 ? "" : "s"}`;
  }

  if (loading) return <div className="flex justify-center py-20"><LoadingSpinner /></div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center flex-shrink-0">
          <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white" aria-hidden="true">
            <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.34 6.34 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.75a8.19 8.19 0 0 0 4.78 1.52V6.84a4.85 4.85 0 0 1-1.01-.15z" />
          </svg>
        </div>
        <div>
          <h2 className="text-lg font-semibold text-slate-800">TikTok Direct Messages</h2>
          <p className="text-sm text-slate-500">
            Connect TikTok Business accounts to receive and respond to DMs in the Velaro inbox.
            Requires a TikTok Business or Creator account enrolled in the TikTok for Business program.
          </p>
        </div>
      </div>

      {/* App not configured warning */}
      {appSettings && !appSettings.configured && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <i className="fas fa-triangle-exclamation text-amber-500 mt-0.5" aria-hidden="true" />
            <div>
              <div className="font-medium text-amber-800">TikTok app credentials not configured</div>
              <div className="text-sm text-amber-700 mt-1">
                The TikTok API credentials (<code className="bg-amber-100 px-1 rounded">TikTok:ClientKey</code> and{" "}
                <code className="bg-amber-100 px-1 rounded">TikTok:ClientSecret</code>) are not set in App Settings.
                Visit the{" "}
                <Link to="/admin/platform-setup" className="underline font-medium">
                  Platform Setup page
                </Link>{" "}
                for setup instructions.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status message */}
      {message && (
        <div
          className={`px-4 py-3 rounded-md text-sm ${
            message.type === "success"
              ? "bg-green-50 border border-green-200 text-green-800"
              : "bg-red-50 border border-red-200 text-red-800"
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Connected accounts */}
      {configs.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full bg-white border border-slate-200 rounded-lg text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Account</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Team</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Token</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Status</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600" style={{ width: 1 }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {configs.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {c.avatarUrl ? (
                        <img
                          src={c.avatarUrl}
                          alt={c.displayName}
                          className="w-8 h-8 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-black flex items-center justify-center">
                          <svg viewBox="0 0 24 24" className="w-4 h-4 fill-white" aria-hidden="true">
                            <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.34 6.34 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.75a8.19 8.19 0 0 0 4.78 1.52V6.84a4.85 4.85 0 0 1-1.01-.15z" />
                          </svg>
                        </div>
                      )}
                      <div>
                        <div className="font-medium text-slate-800">{c.displayName}</div>
                        {c.username && (
                          <div className="text-xs text-slate-500">@{c.username}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{c.teamName ?? (c.teamId ? `Team ${c.teamId}` : "All teams")}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center gap-1 text-xs ${
                        c.tokenValid ? "text-green-700" : "text-red-600"
                      }`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${c.tokenValid ? "bg-green-500" : "bg-red-500"}`}
                      />
                      {c.tokenValid ? formatExpiry(c.tokenExpiresAt) : "Expired — reconnect"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                      Active
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <button
                      className="text-blue-600 hover:text-blue-800 underline text-xs"
                      onClick={() => setEditConfig(c)}
                      aria-label={`Edit ${c.displayName}`}
                    >
                      Edit
                    </button>
                    <span className="mx-2 text-slate-300">|</span>
                    <button
                      className="text-blue-600 hover:text-blue-800 underline text-xs"
                      onClick={handleConnect}
                      aria-label={`Reconnect ${c.displayName}`}
                    >
                      Reconnect
                    </button>
                    <span className="mx-2 text-slate-300">|</span>
                    <button
                      className="text-red-500 hover:text-red-700 underline text-xs"
                      onClick={() => handleRemove(c.id)}
                      aria-label={`Remove ${c.displayName}`}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {configs.length === 0 && !loading && (
        <div className="text-center py-10 bg-slate-50 rounded-lg border border-dashed border-slate-200">
          <div className="w-12 h-12 rounded-full bg-black flex items-center justify-center mx-auto mb-3">
            <svg viewBox="0 0 24 24" className="w-6 h-6 fill-white" aria-hidden="true">
              <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.34 6.34 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.75a8.19 8.19 0 0 0 4.78 1.52V6.84a4.85 4.85 0 0 1-1.01-.15z" />
            </svg>
          </div>
          <p className="text-slate-600 font-medium">No TikTok accounts connected</p>
          <p className="text-slate-400 text-sm mt-1">
            Connect a TikTok Business account to receive DMs in your inbox.
          </p>
        </div>
      )}

      {/* Connect button */}
      <div>
        <PrimaryBlueButton
          label={connecting ? "Redirecting to TikTok..." : "Connect TikTok Account"}
          onClick={handleConnect}
          disabled={connecting || (appSettings !== null && !appSettings.configured)}
        />
        <p className="text-xs text-slate-400 mt-2">
          You'll be redirected to TikTok to authorize Velaro to access your business account's
          messages. TikTok tokens expire every 2 hours and are automatically refreshed. Refresh
          tokens last 365 days, after which you'll need to reconnect.
        </p>
      </div>

      {editConfig !== undefined && (
        <TikTokEditModal
          configId={editConfig.id}
          initialConfig={editConfig}
          onClose={() => setEditConfig(undefined)}
          onSave={() => {
            setEditConfig(undefined);
            fetchConfigs();
          }}
        />
      )}
    </div>
  );
}
